import { Field, FieldProps, Form, Formik, FormikHelpers, FormikProps } from "formik";
import { useContext } from "react";
import { Navigate } from "react-router-dom";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { FormErrorMessage, FormikSubmitButton, TextInput } from "../components/fieldset.tsx";
import { AuthPageHeader } from "../components/frame.tsx";
import { Link } from "../components/link.tsx";
import { SessionContext } from "../session-context.ts";
import { Page, User } from "../types.ts";
import { usePageTitle } from "../utils.ts";
import { SignupFormValues, submitSignupForm } from "./signup.lib.ts";

const initialValues: SignupFormValues = { email: "", name: "", password: "" };

const SignupSchema = toFormikValidationSchema(
  z
    .object({
      email: z
        .string({ required_error: "Email is required" })
        .email("A valid email is required")
        .min(1, "Email is required"),
      name: z.string({ required_error: "Name is required" }).min(1, "Name is required"),
      password: z.string({ required_error: "Password is required" }).min(1, "Password is required"),
    })
    .required({ email: true, name: true, password: true }),
);

export function SignupPage() {
  usePageTitle("Sign up");
  const session = useContext(SessionContext);

  if (session.session.user) {
    return <Navigate to={Page.Plan} />;
  }

  return (
    <>
      <AuthPageHeader
        heading="Sign up for MealsMatter"
        extra=<>
          Already a member? <Link to={Page.Signin}>Sign into your account</Link>
        </>
      />

      <div>
        <Formik
          initialValues={initialValues}
          validationSchema={SignupSchema}
          onSubmit={async (values: SignupFormValues, actions: FormikHelpers<SignupFormValues>) => {
            const user: User | undefined = await submitSignupForm(values, actions);

            session.setSession((s) => {
              return { ...s, user: user };
            });
          }}
        >
          {/*Note this is a function not a component*/}
          {SignupForm}
        </Formik>
      </div>
    </>
  );
}

function SignupForm(props: FormikProps<SignupFormValues>) {
  return (
    <Form className="space-y-6">
      {props.status && <FormErrorMessage>{props.status.message}</FormErrorMessage>}
      <Field name="name">
        {({ field, meta }: FieldProps) => (
          <TextInput
            label="Name"
            inputProps={{ ...field, type: "text", autoComplete: "name" }}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>

      <Field name="email">
        {({ field, meta }: FieldProps) => (
          <TextInput
            label="Email"
            inputProps={{ ...field, type: "email", autoComplete: "email" }}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>

      <Field name="password">
        {({ field, meta }: FieldProps) => (
          <TextInput
            label="Password"
            inputProps={{ ...field, type: "password", autoComplete: "new-password" }}
            error={meta.touched && meta.error ? meta.error : undefined}
          />
        )}
      </Field>

      <FormikSubmitButton
        className="w-full"
        title="Sign Up"
        isSubmitting={props.isSubmitting}
      />
    </Form>
  );
}
